import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  useMediaQuery,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { Edit, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PetFormHeader } from "../components/PetFormHeader";
import NotifyContainer from "./NotifyContainer";
import {
  getPetDeworming,
  createDewormingRecord,
  updateDewormingRecord,
  deleteDewormingRecord,
  PostNotifications,
} from "../services/api";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { validateDate, convertFromISO } from "../utils/dateValidation";
import DatePickerField from "../components/DatePickerField";
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../contexts/NotificationContext';


const Label = ({ children, htmlFor, className, ...props }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    className={className}
    variant="body2"
    color="textPrimary"
    {...props}
    sx={{ fontWeight: 700, mb: 1, ...props.sx }}
  >
    {children}
  </Typography>
);

const PetDeworming = () => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const [isEditMode, setIsEditMode] = useState(false);
  const dewormingFormRef = useRef(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletingDewormingId, setDeletingDewormingId] = useState(null);

  const [showNotifyContainer, setShowNotifyContainer] = useState(false);
  const [newNotification, setNewNotification] = useState(null);
  const { getAllNotifications, addNotification } = useNotifications();

  const [buttonLoading, setButtonLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [BackColor, setBackColor] = useState("");
  const [UpdateMessage, setUpdateMessage] = useState("");
  const { petId } = useParams();
  const location = useLocation();
  const [petData, setPetData] = useState(location.state?.petData);
  const navigate = useNavigate();
  const [dewormingRecords, setDewormingRecords] = useState([]);
  const { t, i18n } = useTranslation();
  const [showDewormingForm, setShowDewormingForm] = useState(false);
  const [editingDewormingId, setEditingDewormingId] = useState(null);
  const [dewormingForm, setDewormingForm] = useState({
    date: "",
    vaccineBrand: "",
    vaccineType: "",
    typesOfInternalParasites: [],
    typesOfExternalParasites: [],
    frequency: "",
    nextDewormDate: "",
    notification: false,
  });
  const [dateErrors, setDateErrors] = useState({});

  const internalParasites = [
    { label: t('RoundWorm'), value: "蛔蟲" },
    { label: t('HookWorm'), value: "鉤蟲" },
    { label: t('Moth'), value: "條蟲" },
    { label: t('HeartThreadWorm'), value: "心絲蟲" },
  ];
  const externalParasites = [
    { label: t('Flea'), value: "跳蚤" },
    { label: t('Bedbug'), value: "壁蝨" },
    { label: t('Ticks'), value: "蜱蟲" },
    { label: t('EarMites'), value: "耳疥蟲" },
  ];

  const dewormingTypesEn = ['Oral anthelmintics', 'Topical drops', 'Injections', 'Drops for both internal and external use'];
  const dewormingTypesZh = ["口服驅蟲劑", "外用滴劑", "注射劑", "內外用滴劑"];
  const dewormingTypes = i18n.language === 'zh' ? dewormingTypesZh : dewormingTypesEn;
  // State to track custom "other" entries
  const [otherInternalParasite, setOtherInternalParasite] = useState("");
  const [otherExternalParasite, setOtherExternalParasite] = useState("");

  // Sorting state
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortedRecords, setSortedRecords] = useState([]);

  const getParasiteLabel = (value) => {
    const allParasites = [...internalParasites, ...externalParasites];
    const parasite = allParasites.find(p => p.value === value);
    return parasite ? parasite.label : value; // Fallback to value if not found
  };

  // Sorting function
  const sortRecordsByDate = (records, order) => {
    return [...records].sort((a, b) => {
      const dateA = new Date(convertFromISO(a.date));
      const dateB = new Date(convertFromISO(b.date));

      if (order === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  };

  // Handle sorting
  const handleSort = () => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);
    const sorted = sortRecordsByDate(dewormingRecords, newOrder);
    setSortedRecords(sorted);
  };

  // Fetch pet data if editing
  useEffect(() => {
    if (location.pathname.includes("edit") && petData) {
      setIsEditMode(true);
      setDewormingRecords(petData.deworm);
    }
  }, [location, petData]);

  // Update sorted records when dewormingRecords or sortOrder changes
  useEffect(() => {
    const sorted = sortRecordsByDate(dewormingRecords, sortOrder);
    setSortedRecords(sorted);
  }, [dewormingRecords, sortOrder]);

  const handleAddNewRecord = () => {
    setShowDewormingForm(true);
    setEditingDewormingId(null);
    setDewormingForm({
      date: "",
      vaccineBrand: "",
      vaccineType: "",
      typesOfInternalParasites: [],
      typesOfExternalParasites: [],
      frequency: "",
      nextDewormDate: "",
      notification: false,
    });
    setOtherInternalParasite("");
    setOtherExternalParasite("");
  };

  const handleEditRecord = (record) => {
    setShowDewormingForm(true);
    setEditingDewormingId(record._id);

    // Separate predefined parasites from custom entries
    const predefinedInternal = internalParasites.map((p) => p.value);
    const predefinedExternal = externalParasites.map((p) => p.value);

    const internalTypes = record.typesOfInternalParasites || [];
    const externalTypes = record.typesOfExternalParasites || [];

    const filteredInternal = internalTypes.filter((type) =>
      predefinedInternal.includes(type)
    );
    const customInternal =
      internalTypes.find((type) => !predefinedInternal.includes(type)) || "";

    setTimeout(() => {
      dewormingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);

    const filteredExternal = externalTypes.filter((type) =>
      predefinedExternal.includes(type)
    );
    const customExternal =
      externalTypes.find((type) => !predefinedExternal.includes(type)) || "";

    const typeIndex = dewormingTypes.findIndex(t => t === record.vaccineType);

    setDewormingForm({
      date: convertFromISO(record.date),
      vaccineBrand: record.vaccineBrand,
      vaccineType: typeIndex >= 0 ? typeIndex : "",
      typesOfInternalParasites: filteredInternal,
      typesOfExternalParasites: filteredExternal,
      frequency: record.frequency,
      nextDewormDate: convertFromISO(record.nextDewormDate),
      notification: record.notification,
    });
    setOtherInternalParasite(customInternal);
    setOtherExternalParasite(customExternal);
  };

  const handleCancelForm = () => {
    setShowDewormingForm(false);
    setEditingDewormingId(null);
    setDewormingForm({
      date: "",
      vaccineBrand: "",
      vaccineType: "",
      typesOfInternalParasites: [],
      typesOfExternalParasites: [],
      frequency: "",
      nextDewormDate: "",
      notification: false,
    });
    setOtherInternalParasite("");
    setOtherExternalParasite("");
    setDateErrors({});
  };

  const handleSubmitForm = async () => {
    setButtonLoading(true);
    try {
      // Validate dates
      if (dewormingForm.date) {
        const dateValidation = validateDate(dewormingForm.date);
        if (!dateValidation.isValid) {
          setButtonLoading(false);
          setError(true);
          setBackColor("#d32f2f");
          setUpdateMessage(t('ErrorSavingPet'));
          setTimeout(() => {
            setError(false);
          }, 2000);
          setDateErrors({ date: dateValidation.error });
          return;
        }
      }

      if (dewormingForm.nextDewormDate) {
        const nextDateValidation = validateDate(dewormingForm.nextDewormDate, true);
        if (!nextDateValidation.isValid) {
          setButtonLoading(false);
          setError(true);
          setBackColor("#d32f2f");
          setUpdateMessage(t('ErrorSavingPet'));
          setTimeout(() => {
            setError(false);
          }, 2000);
          setDateErrors({ nextDewormDate: nextDateValidation.error });
          return;
        }
      }

      // Combine predefined and custom parasites
      const finalInternalParasites = [
        ...dewormingForm.typesOfInternalParasites,
        ...(otherInternalParasite ? [otherInternalParasite] : []),
      ];

      const finalExternalParasites = [
        ...dewormingForm.typesOfExternalParasites,
        ...(otherExternalParasite ? [otherExternalParasite] : []),
      ];

      const dewormingData = {
        date: dewormingForm.date,
        vaccineBrand: dewormingForm.vaccineBrand,
        vaccineType: dewormingForm.vaccineType,
        typesOfInternalParasites: finalInternalParasites,
        typesOfExternalParasites: finalExternalParasites,
        frequency: dewormingForm.frequency,
        nextDewormDate: dewormingForm.nextDewormDate,
        notification: dewormingForm.notification,
      };

      if (editingDewormingId) {
        // Update existing record
        await updateDewormingRecord(petId, editingDewormingId, dewormingData);
      } else {
        // Create new record
        await createDewormingRecord(petId, dewormingData);
      }

      // Refresh data
      const response = await getPetDeworming(petId);
      setDewormingRecords(response.data.form.deworm);
      setPetData((petData) => ({
        ...petData,
        deworm: response.data.form.deworm,
      }));

      const notificationData = {
        userId: petData.userId,
        type: "deworming",
        petId: petId,
        petName: petData.name,
        nextEventDate: dewormingForm.nextDewormDate,
      }

      const notificationResponse = await PostNotifications(notificationData);

      if (notificationResponse.status === 200) {
        // Add to context and show the newly created notification
        if (notificationResponse.data && notificationResponse.data.notification) {
          addNotification(notificationResponse.data.notification);
          setNewNotification(notificationResponse.data.notification);
          setShowNotifyContainer(true);

          // Hide after 3 seconds
          setTimeout(() => {
            setShowNotifyContainer(false);
            setNewNotification(null);
          }, 2000);
        }

        setSuccess(true);
        setButtonLoading(false);
        setBackColor("#4CAF50");
        setUpdateMessage(t('PetUpdated'));
        setTimeout(() => {
          setSuccess(false);
        }, 2000);


      } else {
        setError(true);
        setButtonLoading(false);
        setBackColor("#d32f2f");
        setUpdateMessage(t('ErrorSavingPet'));
        setTimeout(() => {
          setError(false);
        }, 2000);
        return;
      }
      // Reset form
      handleCancelForm();
    } catch (error) {
      setButtonLoading(false);
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error saving deworm record:", error);
    }
  };

  const handleDeleteRecord = async () => {
    setDeleteLoading(true);
    try {
      await deleteDewormingRecord(petId, deletingDewormingId);

      // Refresh data
      const response = await getPetDeworming(petId);
      setDewormingRecords(response.data.form.deworm);
      setPetData((petData) => ({
        ...petData,
        deworm: response.data.form.deworm,
      }));
      setShowDewormingForm(false);
      setSuccess(true);
      setBackColor("#4CAF50");
      setUpdateMessage(t('PetUpdated'));
      setTimeout(() => {
        setSuccess(false);
      }, 2000);

    } catch (error) {
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error deleting deworm record:", error);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FCF8F3",
        minHeight: "100vh",
        pt: isMobile ? 12 : 16,
      }}
    >
      <Navbar />
      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          mb: { xs: 5, md: 10 },
          px: { xs: 2, sm: 3, md: 0 },
        }}
      >
        <PetFormHeader
          currentPageId="deworm"
          isEditMode={isEditMode}
          petId={isEditMode ? petId : null}
          handlePageNavigation={() => { }}
          petData={petData}
        />
        <Box component="form">
          <Typography fontWeight={700} sx={{ fontSize: 18, mb: 4, px: 3 }}>
            {t('DewormingRecords')}
            <Button
              onClick={handleAddNewRecord}
              disabled={showDewormingForm}
              sx={{
                ml: 2,
                fontWeight: 700,
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
              variant="outlined"
              color="text.secondary"
            >
              + {t('AddNewRecord')}
            </Button>
          </Typography>
          {!showDewormingForm && petData.deworm.length === 0 && (
            <Typography fontWeight={700} sx={{ color: "#969696", fontSize: 18, p: 3, display: "flex", justifyContent: "center" }}>{t("NoDewormingRecords")}</Typography>
          )}
          {/* deworm Records Table */}
          {dewormingRecords.length > 0 && (
            <Box sx={{ px: 3, mb: 4 }}>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: 1,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { backgroundColor: "#e8e8e8" },
                          }}
                          onClick={handleSort}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {t('dewormingDate')}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                ml: 0.5,
                              }}
                            >
                              {sortOrder === "asc" ? (
                                <ArrowUp size={16} />
                              ) : (
                                <ArrowDown size={16} />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('vaccineBrand')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t('vaccineType')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t('typesOfInternalParasites')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t('typesOfExternalParasites')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t('frequency')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t('NextDewormingDate')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRecords.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell>{convertFromISO(record.date)}</TableCell>
                          <TableCell>{record.vaccineBrand}</TableCell>
                          <TableCell>{dewormingTypes[record.vaccineType] || "-"}</TableCell>
                          <TableCell>
                            {(record.typesOfInternalParasites || [])
                              .map(value => getParasiteLabel(value))
                              .filter(Boolean)
                              .join(", ") || "-"}
                          </TableCell>
                          <TableCell>
                            {(record.typesOfExternalParasites || [])
                              .map(value => getParasiteLabel(value))
                              .filter(Boolean)
                              .join(", ") || "-"}
                          </TableCell>
                          <TableCell>{record.frequency}</TableCell>
                          <TableCell>
                            {record.nextDewormDate
                              ? convertFromISO(record.nextDewormDate)
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleEditRecord(record)}
                              size="small"
                              sx={{ mr: 1 }}
                            >
                              <Edit size={16} />
                            </IconButton>
                            <IconButton
                              onClick={() => { setDeletingDewormingId(record._id); setShowDeleteModal(true); }}
                              size="small"
                              color="error"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Simple Text View */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", mb: 2, px: 2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#666" }}
                  >
                    {t('sortby')}
                  </Typography>
                  <Button
                    onClick={handleSort}
                    size="small"
                    sx={{
                      ml: 1,
                      color: "#FFB60C",
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { backgroundColor: "rgba(255,182,12,0.08)" },
                    }}
                  >
                    {t('dewormingDate')} {sortOrder === "asc" ? "▲" : "▼"}
                  </Button>
                </Box>
                {sortedRecords.map((record, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 4,
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, fontSize: 16, color: "#333" }}
                      >
                        {t('DewormingRecords')}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          onClick={() => handleEditRecord(record)}
                          size="small"
                          sx={{ color: "#FFB60C" }}
                        >
                          <Edit size={20} />
                        </IconButton>
                        <IconButton
                          onClick={() => { setDeletingDewormingId(record._id); setShowDeleteModal(true); }}
                          size="small"
                          sx={{ color: "#A0A0A0" }}
                        >
                          <Trash2 size={20} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('dewormingDate')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {convertFromISO(record.date) || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('vaccineBrand')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.vaccineBrand || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('vaccineType')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {dewormingTypes[record.vaccineType] || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('typesOfInternalParasites')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {(record.typesOfInternalParasites || [])
                            .filter(Boolean)
                            .map(value => getParasiteLabel(value))
                            .join(", ") || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('typesOfExternalParasites')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {(record.typesOfExternalParasites || [])
                            .filter(Boolean)
                            .map(value => getParasiteLabel(value))
                            .join(", ") || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('frequency')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.frequency || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('NextDewormingDate')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.nextDewormDate
                            ? convertFromISO(record.nextDewormDate)
                            : "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* deworm Form */}
          {showDewormingForm && (
            <>
              <Grid
                ref={dewormingFormRef}
                container
                spacing={3}
                justifyContent="flex-start"
                sx={{ px: 3, backgroundColor: "#FFF2E2", py: 3 }}
              >
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="dewormingDate">{t('dewormingDate')}</Label>
                  <DatePickerField
                    id="dewormingDate"
                    name="dewormingDate"
                    placeholder="DD/MM/YYYY"
                    value={dewormingForm.date}
                    onChange={(value) =>
                      setDewormingForm((prev) => ({ ...prev, date: value }))
                    }
                    onError={(error) =>
                      setDateErrors({ ...dateErrors, date: error })
                    }
                    error={!!dateErrors.date}
                    helperText={dateErrors.date}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="brand">{t('vaccineBrand')}</Label>
                  <TextField
                    id="brand"
                    name="brand"
                    placeholder={t('vaccineBrand')}
                    value={dewormingForm.vaccineBrand}
                    onChange={(e) =>
                      setDewormingForm((prev) => ({
                        ...prev,
                        vaccineBrand: e.target.value,
                      }))
                    }
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="dewormingType">{t('vaccineType')}</Label>
                  <Select
                    id="dewormingType"
                    name="dewormingType"
                    value={dewormingForm.vaccineType}
                    onChange={(e) =>
                      setDewormingForm((prev) => ({
                        ...prev,
                        vaccineType: parseInt(e.target.value, 10),
                      }))
                    }
                    displayEmpty
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      {t('PleaseSelect')}
                    </MenuItem>
                    {dewormingTypes.map((type, index) => (
                      <MenuItem key={index} value={index}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={3}
                justifyContent="flex-start"
                sx={{ px: 3, backgroundColor: "#FFF2E2", }}
              >
                <Grid item xs={12} md={8} sx={{ maxWidth: 820, width: "100%" }}>
                  <Label>{t('typesOfInternalParasites')}</Label>
                  <FormGroup row={!isMobile} sx={{ gap: isMobile ? 0 : 2 }}>
                    {internalParasites.map((p) => (
                      <FormControlLabel
                        key={p.value}
                        control={
                          <Checkbox
                            checked={dewormingForm.typesOfInternalParasites.includes(
                              p.value
                            )}
                            onChange={(e) => {
                              setDewormingForm((prev) => {
                                const newInternal = e.target.checked
                                  ? [...prev.typesOfInternalParasites, p.value]
                                  : prev.typesOfInternalParasites.filter(
                                    (v) => v !== p.value
                                  );

                                return {
                                  ...prev,
                                  typesOfInternalParasites: newInternal,
                                };
                              });
                            }}
                          />
                        }
                        label={p.label}
                      />
                    ))}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!otherInternalParasite}
                          onChange={(e) => {
                            if (!e.target.checked) {
                              setOtherInternalParasite("");
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {t('Other')}：
                          <TextField
                            value={otherInternalParasite}
                            onChange={(e) =>
                              setOtherInternalParasite(e.target.value)
                            }
                            placeholder=""
                            size="small"
                            sx={{ ml: 1, width: 80, backgroundColor: "white" }}
                          />
                        </Box>
                      }
                    />
                  </FormGroup>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={3}
                justifyContent="flex-start"
                sx={{ px: 3, backgroundColor: "#FFF2E2", py: 3 }}
              >
                <Grid item xs={12} md={8} sx={{ maxWidth: 700, width: "100%" }}>
                  <Label>{t('typesOfExternalParasites')}</Label>
                  <FormGroup row={!isMobile} sx={{ gap: isMobile ? 0 : 2 }}>
                    {externalParasites.map((p) => (
                      <FormControlLabel
                        key={p.value}
                        control={
                          <Checkbox
                            checked={dewormingForm.typesOfExternalParasites.includes(
                              p.value
                            )}
                            onChange={(e) => {
                              setDewormingForm((prev) => {
                                const newExternal = e.target.checked
                                  ? [...prev.typesOfExternalParasites, p.value]
                                  : prev.typesOfExternalParasites.filter(
                                    (v) => v !== p.value
                                  );

                                return {
                                  ...prev,
                                  typesOfExternalParasites: newExternal,
                                };
                              });
                            }}
                          />
                        }
                        label={p.label}
                      />
                    ))}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!otherExternalParasite}
                          onChange={(e) => {
                            if (!e.target.checked) {
                              setOtherExternalParasite("");
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {t('Other')}：
                          <TextField
                            value={otherExternalParasite}
                            onChange={(e) =>
                              setOtherExternalParasite(e.target.value)
                            }
                            placeholder=""
                            size="small"
                            sx={{ ml: 1, width: 80, backgroundColor: "white" }}
                          />
                        </Box>
                      }
                    />
                  </FormGroup>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={3}
                justifyContent="flex-start"
                sx={{ px: 3, backgroundColor: "#FFF2E2", }}
              >
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="frequency">{t('frequency')}</Label>
                  <TextField
                    id="frequency"
                    name="frequency"
                    placeholder={t('frequency')}
                    value={dewormingForm.frequency}
                    onChange={(e) =>
                      setDewormingForm((prev) => ({
                        ...prev,
                        frequency: e.target.value,
                      }))
                    }
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="nextDate">{t('NextDewormingDate')}</Label>
                  <DatePickerField
                    id="nextDate"
                    name="nextDate"
                    placeholder="DD/MM/YYYY"
                    value={dewormingForm.nextDewormDate}
                    onChange={(value) =>
                      setDewormingForm((prev) => ({
                        ...prev,
                        nextDewormDate: value,
                      }))
                    }
                    onError={(error) =>
                      setDateErrors({ ...dateErrors, nextDewormDate: error })
                    }
                    error={!!dateErrors.nextDewormDate}
                    helperText={dateErrors.nextDewormDate}
                    allowFutureDates={true}
                  />
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  gap: 2,
                  px: 3,
                  backgroundColor: "#FFF2E2", py: 3
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  disabled={buttonLoading}
                  sx={{
                    backgroundColor: "#FFB60C",
                    color: "#050505",
                    minWidth: 120,
                    "&:hover": { backgroundColor: "#FFB60C" },
                    "&:disabled": { background: "rgba(255, 182, 12, 0.5)" },
                    cursor: buttonLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                  onClick={handleSubmitForm}
                >
                  {t('Save')}
                  {buttonLoading && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <CircularProgress size={15} color="inherit" />
                    </span>
                  )}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ color: "#A0A0A0", minWidth: 120, fontWeight: 700 }}
                  onClick={handleCancelForm}
                >
                  {t('cancel')}
                </Button>
              </Box>
            </>
          )}

          {/* Bottom Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              mt: { xs: 5, md: 10 },
              mr: 3,
              gap: 2,
            }}
          >
            <Button
              type="button"
              variant="outlined"
              color="text.secondary"
              onClick={() => navigate("/pet-management")}
              sx={{
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
            >
              {t('BackToHome')}
            </Button>
          </Box>
        </Box>
      </Box>
      <Footer />
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: (success || error) ? 20 : -400,
          backgroundColor: BackColor,
          color: "#fff",
          padding: "16px 24px",
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9999,
          minWidth: 250,
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: (success || error)
            ? "right 0.4s ease-out"
            : "right 0.4s ease-in",
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {error ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
        <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
          {UpdateMessage}
        </Typography>
      </Box>
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            p: 3,
            m: 2,
            boxSizing: "border-box",
            textAlign: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 20,
            justifyContent: "center",
            gap: 1,
          }}
        >

          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.2947 3.86118L1.82466 18.0012C1.65003 18.3036 1.55763 18.6465 1.55665 18.9957C1.55567 19.3449 1.64615 19.6883 1.81909 19.9917C1.99202 20.2951 2.24139 20.5479 2.54237 20.725C2.84335 20.9021 3.18546 20.9973 3.53466 21.0012H20.4747C20.8239 20.9973 21.166 20.9021 21.467 20.725C21.7679 20.5479 22.0173 20.2951 22.1902 19.9917C22.3632 19.6883 22.4536 19.3449 22.4527 18.9957C22.4517 18.6465 22.3593 18.3036 22.1847 18.0012L13.7147 3.86118C13.5364 3.56729 13.2854 3.3243 12.9859 3.15567C12.6863 2.98703 12.3484 2.89844 12.0047 2.89844C11.6609 2.89844 11.323 2.98703 11.0235 3.15567C10.7239 3.3243 10.4729 3.56729 10.2947 3.86118Z" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 9V13" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 17H12.008" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>

          {t('ConfirmDeleteDeworming')}
        </Box>
        <DialogContent sx={{ pt: 0, pb: 0 }}>
          <Typography
            variant="body1"
            sx={{ mb: 3, textAlign: "center", fontSize: 16 }}
          >
            {t('Systemwilldelete')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: "column",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#FF1A1A",
              color: "#050505",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              mb: 1,
              textAlign: "center",
              textTransform: 'none',
              "&:hover": { backgroundColor: "#d63e3eff" },
              cursor: deleteLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
            onClick={handleDeleteRecord}
          >
            {t('Delete')}
            {deleteLoading && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <CircularProgress size={15} color="inherit" />
              </span>
            )}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{
              color: "#B0B0B0",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              textAlign: "center",
              textTransform: 'none',
            }}
            onClick={() => setShowDeleteModal(false)}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
      <NotifyContainer
        open={showNotifyContainer}
        onClose={() => {
          setShowNotifyContainer(false);
          setNewNotification(null);
        }}
        singleNotification={newNotification}
      />
    </Box>
  );
};

export default PetDeworming;
